import { FC, useMemo, useEffect } from 'react'
import cn from 'classnames'
import styles from 'components/features/external/PublicSpendingByStateChart/PublicSpendingByStateChart.styl'
import { GeoChart } from 'components/features/pfm/GeoChart/GeoChart'
import { USStateCollection, USStates } from 'constants/geo'
import { KeyValueListItem, KeyValueList } from 'components/features/pfm/KeyValueList/KeyValueList'
import { getItemsFromCollection, getItemFromCollection } from 'helpers/normalizeHelpers'
import { useStoreSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import { publicFiltersSelector } from 'selectors/publicDashboardSelectors'
import { getPublicBreakdownByStateActionCreator } from 'actions/publicDashboardActions'
import { isFetchableLoading } from 'helpers/loadableHelpers'
import LoadingOverlay from 'components/atoms/preloaders/LoadingOverlay'
import { USStateCode } from 'interfaces/geo'
import { IdValueEntity } from 'interfaces'
import { isMobileSelector } from 'selectors/viewportSelectors'
import { MOBILE_KEYVALUE_ITEMS_LIMIT } from 'constants/publicDashboard'

const GEO_DATA_HEADER = ['State', 'Value', { role: 'tooltip', p: { html: true }}]
const RED = 'rgb(227, 66, 66)'
const YELLOW = 'rgb(242, 164, 34)'
const GREEN = 'rgb(87, 189, 57)'
const BASE_GEO_MAP_OPTIONS = {
  region: 'US',
  displayMode: 'state',
  resolution: 'provinces',
}

const getTooltipHTML = (entity: IdValueEntity<USStateCode, number>): string => {
  const { id, value } = entity

  const arrow = value > 0 ? '↑' : value < 0 ? '↓' : ''
  const arrowClasses = cn(styles.arrow, { [styles.red]: value < 0 })

  return `
    <div class='${ styles.chartTooltip }'>
      <div class='${ styles.label }'>
        Spending change
      </div>
      <div class='${ styles.value }'>
        ${ id }:
        <span class='${ arrowClasses }'>${ arrow }</span>
       ${ Math.abs(value) }%
      </div>
    </div>
  `
}

const getAxisDefinition = (min: number, max: number) => {
  const axisColors: string[] = []
  const axisValues: number[] = []

  if (Math.sign(min) === Math.sign(max)) {
    axisValues.push(min, max)
    if (min < 0) {
      axisColors.push(RED, YELLOW)
    } else {
      axisColors.push(YELLOW, GREEN)
    }

  } else {
    if (min < 0) {
      axisColors.push(RED)
      axisValues.push(min)
    }
    axisColors.push(YELLOW)
    axisValues.push(0)
    if (max > 0) {
      axisColors.push(GREEN)
      axisValues.push(max)
    }
  }

  return {
    colors: axisColors,
    values: axisValues,
  }
}

const US_STATE_CODES_SET = new Set(USStates.map(({ code }) => code))

type PublicSpendingByStateChart = {

}

export const PublicSpendingByStateChart: FC<PublicSpendingByStateChart> = () => {
  const dispatch = useStoreDispatch()

  const { incomeRange, category } = useStoreSelector(publicFiltersSelector)
  const byStateFetchable = useStoreSelector((state) => state.data.publicdashboard.breakdownByState)
  const isMobile = useStoreSelector(isMobileSelector)

  useEffect(() => {
    dispatch(getPublicBreakdownByStateActionCreator.request({ incomeRange, category }))
  }, [dispatch, incomeRange, category])

  const preparedData = useMemo(() => {
    if (!byStateFetchable.data) {
      return null
    }

    const { items } = byStateFetchable.data


    const dataArray = getItemsFromCollection<IdValueEntity<USStateCode, number>, 'id'>(items).filter(({ id }) => US_STATE_CODES_SET.has(id))

    const mapValues = dataArray.map(({ value }) => value)
    const min = Math.min(...mapValues)
    const max = Math.max(...mapValues)

    // TODO: optimize `.map`
    const chartOptions = {
      ...BASE_GEO_MAP_OPTIONS,
      colorAxis: getAxisDefinition(min, max),
      legend: {
        numberFormat: '00\'%\'',
      },
      tooltip: {
        isHtml: true,
      },
    }

    const data = dataArray.map(({ id, value }) => {
      return [
        id,
        value,
        getTooltipHTML({ id, value }),
      ]
    })

    const listItems = dataArray.reduce<KeyValueListItem[]>(
      (acc, { id, value }) => {
        const state = getItemFromCollection(USStateCollection, id)
        if (!state) {
          return acc
        }

        const item: KeyValueListItem = {
          name: state ? state.name : 'unknown',
          value: `${ Math.abs(value) }%`,
          dynamics: value > 0 ? 'positive' : (value < 0 ? 'negative' : null),
        }

        acc.push(item)

        return acc
      },
      []
    )

    return {
      options: chartOptions,
      chartData: [GEO_DATA_HEADER, ...data],
      listItems,
    }
  }, [byStateFetchable])

  const preloaderChild = isFetchableLoading(byStateFetchable) && (
    <LoadingOverlay />
  )

  const geoChartWrapperChild = preparedData
    ? (
      <div className={ styles.chartWrapper }>
        <div className={ styles.chartWrapperInner }>
          <GeoChart
            options={ preparedData.options }
            data={ preparedData.chartData }
          />
        </div>
      </div>
    )
    : null

  const itemsChild = useMemo(() => {
    if (!preparedData) {
      return null
    }

    const limit = isMobile ? MOBILE_KEYVALUE_ITEMS_LIMIT : undefined
    return (
      <div className={ styles.listWrapper }>
        <KeyValueList
          items={ preparedData.listItems }
          isScrollable={ !isMobile }
          limit={ limit }
        />
      </div>
    )


  }, [preparedData, isMobile])

  return (
    <div className={ cn(styles.publicSpendingByStateChart, !isMobile && styles.isDesktop) }>
      <div className={ styles.inner }>
        { geoChartWrapperChild }
        { itemsChild }
        { preloaderChild }
      </div>
    </div>
  )
}
